import { Effect } from "effect";
import * as E from "fp-ts/Either";
import type { OptionTs, TaskEitherTs } from "@ps-types/Ouroboros.Services.Clients.Interop";
import type { Either as OpaqueEither } from "@ps-types/Data.Either";
import * as TaskEither from "@purescript-output/Ouroboros.Services.Clients.Interop.TaskEither";
import { getPendingEmailValidation as getPendingEmailValidationPS } from "@purescript-output/Ouroboros.Services.Ouroboros.PendingEmailValidation";
import { createDefaultAccountClient as createDefaultAccountClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.Account";
import { createDefaultAuthenticationClient as createDefaultAuthenticationClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.Authentication";
import { createDefaultClientProjectClient as createDefaultClientProjectClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.ClientProject";
import { createDefaultContentClient as createDefaultContentClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.Content";
import { createDefaultFeedbackClient as createDefaultFeedbackClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.Feedback";
import { createDefaultAdminPlanClient as createDefaultAdminPlanClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.AdminPlan";
import { createStubAdminPlanClient as createStubAdminPlanClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.AdminPlanStub";
import { createDefaultAdminUserClient as createDefaultAdminUserClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.AdminUsers";
import { createStubAdminUserClient as createStubAdminUserClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.AdminUsersStub";
import { createDefaultEmailValidationClient as createDefaultEmailValidationClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.EmailValidation";
import { createDefaultLifecycleClient as createDefaultLifecycleClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.Lifecycle";
import { createDefaultPaymentClient as createDefaultPaymentClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.Payment";
import { createDefaultOrganizationSignupClient as createDefaultOrganizationSignupClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.OrganizationSignup";
import { createStubOrganizationSignupClient as createStubOrganizationSignupClientPS } from "@purescript-output/Ouroboros.Services.Ouroboros.OrganizationSignupStub";
import * as PSOption from "@purescript-output/Ouroboros.Services.Clients.Interop.Option";
import * as DataEither from "@purescript-output/Data.Either";
// eslint-disable-next-line no-restricted-imports, no-ps-maybe-types/no-ps-maybe-types
import * as DataMaybe from "@purescript-output/Data.Maybe";
import * as DataInt from "@purescript-output/Data.Int";
import * as DocumentStatus from "@purescript-output/Ouroboros.Models.Documents.DocumentStatus";

type PsMaybeRuntime = {
  readonly Nothing: { readonly value: OptionTs<never> };
  readonly Just: { readonly create: <A>(value: A) => OptionTs<A> };
  readonly fromMaybe: <A>(fallback: A) => (maybe: OptionTs<A>) => A;
};

const psMaybeRuntime = DataMaybe as never as PsMaybeRuntime;

export type PSEither<L, R> =
  | { readonly value0: L; readonly constructor: { readonly name: "Left" } }
  | { readonly value0: R; readonly constructor: { readonly name: "Right" } };

const isRight = <L, R>(
  value: PSEither<L, R>
): value is { readonly value0: R; readonly constructor: { readonly name: "Right" } } =>
  value.constructor.name === "Right";

const isLeft = <L, R>(
  value: PSEither<L, R>
): value is { readonly value0: L; readonly constructor: { readonly name: "Left" } } =>
  value.constructor.name === "Left";

export const effectFromPSEither = <L, R, E>(
  either: PSEither<L, R>,
  onLeft: (left: L) => E
): Effect.Effect<R, E> => {
  if (isRight(either)) {
    return Effect.succeed(either.value0 as R);
  }
  if (isLeft(either)) {
    return Effect.fail(onLeft(either.value0 as L));
  }
  return Effect.fail(onLeft(undefined as L));
};

export const eitherFromPSEither = <L, R, E>(
  either: PSEither<L, R>,
  onLeft: (left: L) => E
): E.Either<E, R> => {
  if (isRight(either)) {
    return E.right(either.value0 as R);
  }
  if (isLeft(either)) {
    return E.left(onLeft(either.value0 as L));
  }
  return E.left(onLeft(undefined as L));
};

export const taskEitherFromPSEither = <L, R, E>(
  either: PSEither<L, R>,
  onLeft: (left: L) => E
): TaskEitherTs<E, R> => {
  if (isRight(either)) {
    return TaskEither.right(either.value0 as R);
  }
  if (isLeft(either)) {
    return TaskEither.left(onLeft(either.value0 as L));
  }
  return TaskEither.left(onLeft(undefined as L));
};

export const foldOpaqueEither = <L, R, A>(
  either: OpaqueEither<L, R>,
  onLeft: (left: L) => A,
  onRight: (right: R) => A,
): A => DataEither.either(onLeft, onRight)(either) as A;

export const getPendingEmailValidation = () => getPendingEmailValidationPS(undefined);

export const createDefaultAccountClient = createDefaultAccountClientPS;

export const createDefaultAuthenticationClient = createDefaultAuthenticationClientPS;

export const createDefaultClientProjectClient = createDefaultClientProjectClientPS;

export const createDefaultContentClient = createDefaultContentClientPS;

export const createDefaultFeedbackClient = createDefaultFeedbackClientPS;

export const createDefaultAdminPlanClient = createDefaultAdminPlanClientPS;

export const createStubAdminPlanClient = createStubAdminPlanClientPS;

export const createDefaultAdminUserClient = createDefaultAdminUserClientPS;

export const createStubAdminUserClient = createStubAdminUserClientPS;

export const createDefaultEmailValidationClient = createDefaultEmailValidationClientPS;

export const createDefaultLifecycleClient = createDefaultLifecycleClientPS;

export const createDefaultPaymentClient = createDefaultPaymentClientPS;

export const createDefaultOrganizationSignupClient = createDefaultOrganizationSignupClientPS;

export const createStubOrganizationSignupClient = createStubOrganizationSignupClientPS;

export const optionSome = PSOption.some;
export const optionNone = () => PSOption.none();
export const optionMap = PSOption.map;
export const optionChain = PSOption.chain;
export const optionFold = PSOption.fold;
export const optionToPsMaybe = <A>(option: OptionTs<A>): OptionTs<A> =>
  PSOption.fold(
    () => psMaybeRuntime.Nothing.value as OptionTs<A>,
  )(
    (value: A) => psMaybeRuntime.Just.create(value),
  )(option);
export const psMaybeFromMaybe = <A>(fallback: A) => (maybe: OptionTs<A>): A =>
  psMaybeRuntime.fromMaybe(fallback)(maybe);

export const intRound = DataInt.round;
export const documentStatusVisual = DocumentStatus.statusVisual;
